import { Package, Truck, ShieldCheck, Calculator, CheckCircle2, ArrowDown } from "lucide-react";
import { CIF } from "@/constants/testIds";

const STEPS = [
  {
    key: "factory-price",
    icon: Package,
    label: "Factory Price",
    value: "USD / MT",
    desc: "Lock in your supplier's FOB price as the baseline. Compare across mills, factories and origins.",
  },
  {
    key: "estimated-freight",
    icon: Truck,
    label: "Estimated Freight",
    value: "Container · Lane",
    desc: "Use live indicative freight rates from FreightIQ™ for your destination and container type.",
  },
  {
    key: "estimated-insurance",
    icon: ShieldCheck,
    label: "Estimated Insurance",
    value: "% of CIF",
    desc: "Apply your insurer's slab or a sensible % to estimate marine insurance accurately.",
  },
  {
    key: "estimated-cif",
    icon: Calculator,
    label: "Estimated CIF",
    value: "Total Landed Cost",
    desc: "The Workspace combines all three into a single, defensible landed-cost number.",
    highlight: true,
  },
  {
    key: "purchase-decision",
    icon: CheckCircle2,
    label: "Purchase Decision",
    value: "Commit · Compare · Defer",
    desc: "Approve the PO, compare destinations, or renegotiate — with full landed-cost confidence.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how"
      data-testid={CIF.how}
      className="relative py-24 lg:py-32 bg-slate-50 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> How it works
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-medium tracking-[-0.025em] leading-[1.05] text-slate-900 mt-4">
            Five inputs. One defensible landed cost.
          </h2>
          <p className="text-slate-500 mt-5 text-[15px] leading-relaxed">
            Estimated CIF™ runs as a connected workflow inside your Workspace — not a spreadsheet. Each step feeds the next, automatically.
          </p>
        </div>

        <div className="mt-16 relative">
          {/* connector line */}
          <div className="absolute left-1/2 -translate-x-1/2 top-12 bottom-12 w-px connector-line hidden lg:block" />

          <div className="space-y-6">
            {STEPS.map((s, i) => {
              const isRight = i % 2 === 1;
              return (
                <div
                  key={s.key}
                  data-testid={CIF.howStep(i + 1)}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center"
                >
                  <div className={isRight ? "lg:order-2" : ""}>
                    <div
                      className={[
                        "relative rounded-2xl bg-white border p-7 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(15,23,42,0.18)]",
                        s.highlight
                          ? "border-blue-200 ring-2 ring-blue-500/20 shadow-[0_20px_50px_-25px_rgba(37,99,235,0.45)]"
                          : "border-slate-200 shadow-sm",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between">
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
                          <s.icon className="h-5 w-5" />
                        </div>
                        <span className="mono text-[10px] tracking-widest uppercase text-slate-400 font-semibold">
                          Step {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="mt-5">
                        <div className="font-display text-2xl font-medium tracking-tight text-slate-900 flex items-center gap-2">
                          {s.label}
                          {s.highlight && (
                            <span className="text-[10px] tracking-widest uppercase font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                              Output
                            </span>
                          )}
                        </div>
                        <div className="mono text-[11px] tracking-wider uppercase text-slate-400 mt-1">
                          {s.value}
                        </div>
                        <p className="mt-4 text-[14px] text-slate-600 leading-relaxed">
                          {s.desc}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* dot in center */}
                  <div className={`hidden lg:flex justify-center ${isRight ? "lg:order-1" : ""}`}>
                    <div className="relative">
                      <div className={[
                        "h-12 w-12 rounded-full flex items-center justify-center font-display font-semibold",
                        s.highlight ? "bg-blue-600 text-white shadow-[0_0_0_8px_rgba(37,99,235,0.12)]" : "bg-white text-slate-900 border border-slate-300",
                      ].join(" ")}>
                        {i + 1}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
